import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiX, FiRefreshCw, FiClock, FiFileText, FiEdit2 } from 'react-icons/fi';
import { API_URL, getValidToken } from '../../../utils/authUtils';
import StatusStepper from './StatusStepper';

// Lazy imports for phase panels (will be created in subsequent phases)
import TriagePanel from './TriagePanel';
import CandidateSurfacingPanel from './CandidateSurfacingPanel';
import OutreachPanel from './OutreachPanel';
import ConsentPanel from './ConsentPanel';
import SessionPanel from './SessionPanel';
import FeedbackClosurePanel from './FeedbackClosurePanel';
import AuditLogPanel from './AuditLogPanel';

/**
 * RequestWorkflowModal - Top-level orchestrator for the staff request processing workflow.
 *
 * Replaces the inline request detail modal in AdminExecutiveIntelligence.js.
 * Routes to the correct phase panel based on the request's current status.
 *
 * Props:
 *   requestId: UUID string
 *   onClose: () => void
 *   onRefreshQueue: () => void
 */

// Maps status to which panel to render
const STATUS_PANEL_MAP = {
  SUBMITTED: 'triage',
  TRIAGE: 'triage',
  IN_REVIEW: 'candidates',
  APPROVED: 'candidates',
  STAGED: 'outreach',
  SENT: 'outreach',
  AWAITING: 'consent',
  CONSENTED: 'session',
  SCHEDULED: 'session',
  CONNECTED: 'session',
  CLOSED: 'summary',
  EXPIRED: 'summary',
  DECLINED: 'summary',
  WITHDRAWN: 'summary',
};

// Status badge colors
const STATUS_COLORS = {
  SUBMITTED: 'bg-yellow-100 text-yellow-700',
  TRIAGE: 'bg-blue-100 text-blue-700',
  IN_REVIEW: 'bg-indigo-100 text-indigo-700',
  APPROVED: 'bg-green-100 text-green-700',
  STAGED: 'bg-purple-100 text-purple-700',
  SENT: 'bg-cyan-100 text-cyan-700',
  AWAITING: 'bg-orange-100 text-orange-700',
  CONSENTED: 'bg-teal-100 text-teal-700',
  SCHEDULED: 'bg-sky-100 text-sky-700',
  CONNECTED: 'bg-emerald-100 text-emerald-700',
  CLOSED: 'bg-gray-100 text-gray-600',
  EXPIRED: 'bg-orange-100 text-orange-600',
  DECLINED: 'bg-red-100 text-red-700',
  WITHDRAWN: 'bg-gray-100 text-gray-500',
};

const getErrorMessage = (err, defaultMsg) => {
  if (err.response?.data) {
    const detail = err.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((e) => e.msg || e.message || 'Error').join(', ');
    if (detail?.msg) return detail.msg;
    if (detail?.message) return detail.message;
  }
  return defaultMsg;
};

const RequestWorkflowModal = ({ requestId, onClose, onRefreshQueue }) => {
  // Core state
  const [requestDetail, setRequestDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Timeline
  const [timeline, setTimeline] = useState([]);

  // Audit log toggle
  const [showAuditLog, setShowAuditLog] = useState(false);

  // Staff notes inline edit
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  // Auth helper
  const authHeaders = useCallback(() => {
    const token = getValidToken();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  // Fetch request detail
  const fetchRequestDetail = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError(null);
    try {
      const [detailRes, timelineRes] = await Promise.all([
        axios.get(`${API_URL}/api/staff/brokerage/requests/${requestId}`, authHeaders()),
        axios.get(`${API_URL}/api/staff/brokerage/requests/${requestId}/timeline`, authHeaders()).catch(() => ({ data: { timeline: [] } })),
      ]);
      setRequestDetail(detailRes.data);
      setTimeline(timelineRes.data.timeline || []);
      setNotesValue(detailRes.data.staff_notes || '');
    } catch (err) {
      console.error('Error fetching request detail:', err);
      setError(getErrorMessage(err, 'Failed to load request details'));
    } finally {
      setLoading(false);
    }
  }, [requestId, authHeaders]);

  useEffect(() => {
    fetchRequestDetail();
  }, [fetchRequestDetail]);

  // Callback for child panels after any mutation
  const handleRequestUpdated = useCallback(() => {
    fetchRequestDetail();
    if (onRefreshQueue) onRefreshQueue();
  }, [fetchRequestDetail, onRefreshQueue]);

  // Save staff notes
  const saveStaffNotes = async () => {
    if (!requestDetail) return;
    setNotesSaving(true);
    try {
      await axios.patch(
        `${API_URL}/api/staff/brokerage/requests/${requestId}`,
        { staff_notes: notesValue, row_version: requestDetail.row_version },
        authHeaders()
      );
      await fetchRequestDetail();
      setEditingNotes(false);
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Conflict: This request was modified by another user. Refreshing...');
        await fetchRequestDetail();
      } else {
        setError(getErrorMessage(err, 'Failed to save notes'));
      }
    } finally {
      setNotesSaving(false);
    }
  };

  // Determine which panel to show
  const activePanel = requestDetail ? STATUS_PANEL_MAP[requestDetail.status] || 'summary' : null;

  // Format age
  const getRequestAge = () => {
    if (!requestDetail?.created_at) return '';
    const created = new Date(requestDetail.created_at);
    const now = new Date();
    const diffMs = now - created;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    return `${diffDays} days ago`;
  };

  // Render active panel
  const renderPanel = () => {
    if (!requestDetail) return null;

    const commonProps = {
      requestDetail,
      requestId,
      onRequestUpdated: handleRequestUpdated,
      authHeaders,
    };

    switch (activePanel) {
      case 'triage':
        return <TriagePanel {...commonProps} />;
      case 'candidates':
        return <CandidateSurfacingPanel {...commonProps} />;
      case 'outreach':
        return <OutreachPanel {...commonProps} />;
      case 'consent':
        return <ConsentPanel {...commonProps} />;
      case 'session':
        return <SessionPanel {...commonProps} />;
      case 'summary':
        return <FeedbackClosurePanel {...commonProps} />;
      default:
        return (
          <div className="text-center text-gray-500 py-12">
            <p>Unknown status: {requestDetail.status}</p>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                Request Workflow
              </h2>
              {requestDetail && (
                <p className="text-xs text-gray-500 font-mono">
                  {requestDetail.id?.substring(0, 8)}...
                  <span className="ml-2 text-gray-400">
                    {({'PEER_MID_LEVEL':'Peer Networking','PEER_SENIOR':'Peer Networking (Senior)','MENTORSHIP_UP':'Seek Mentorship','MENTORSHIP_HIGH':'Offer Mentorship','BOARD_PATHWAY':'Board Pathway','HBCU_MENTORSHIP':'HBCU Mentorship'})[requestDetail.request_type] || requestDetail.request_type?.replace(/_/g, ' ')}
                  </span>
                </p>
              )}
            </div>
            {requestDetail && (
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[requestDetail.status] || 'bg-gray-100 text-gray-600'}`}>
                {requestDetail.status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchRequestDetail}
              className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
              title="Refresh"
            >
              <FiRefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex">
          {loading ? (
            <div className="flex items-center justify-center w-full py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mr-3" />
              <span className="text-gray-500">Loading request details...</span>
            </div>
          ) : error && !requestDetail ? (
            <div className="flex flex-col items-center justify-center w-full py-16">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchRequestDetail}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
              >
                Retry
              </button>
            </div>
          ) : requestDetail ? (
            <>
              {/* Left Sidebar */}
              <div className="w-56 border-r border-gray-200 bg-gray-50 overflow-y-auto flex-shrink-0 p-4 space-y-5">
                {/* Status Stepper */}
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Progress</p>
                  <StatusStepper currentStatus={requestDetail.status} timeline={timeline} />
                </div>

                {/* Priority */}
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Priority</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          requestDetail.priority_score >= 0.8 ? 'bg-red-500' :
                          requestDetail.priority_score >= 0.6 ? 'bg-orange-500' :
                          requestDetail.priority_score >= 0.4 ? 'bg-yellow-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${(requestDetail.priority_score || 0.5) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-600">
                      {((requestDetail.priority_score || 0.5) * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>

                {/* Quick Info */}
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Requester</p>
                  <p className="text-xs font-medium text-gray-800 truncate">
                    {requestDetail.requester_name || 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Target</p>
                  <p className="text-xs font-medium text-gray-800 truncate">
                    {requestDetail.target_name || (
                      <span className="italic text-gray-400">Not selected</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Created</p>
                  <p className="text-xs text-gray-600">{getRequestAge()}</p>
                </div>

                {/* Staff Notes */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Notes</p>
                    {!editingNotes && (
                      <button
                        onClick={() => setEditingNotes(true)}
                        className="p-0.5 text-gray-400 hover:text-indigo-600"
                        title="Edit notes"
                      >
                        <FiEdit2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {editingNotes ? (
                    <div className="space-y-1">
                      <textarea
                        value={notesValue}
                        onChange={(e) => setNotesValue(e.target.value)}
                        rows={3}
                        className="w-full text-xs border border-gray-300 rounded p-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Add staff notes..."
                      />
                      <div className="flex gap-1">
                        <button
                          onClick={saveStaffNotes}
                          disabled={notesSaving}
                          className="flex-1 px-2 py-1 bg-indigo-600 text-white text-[10px] rounded hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {notesSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => {
                            setEditingNotes(false);
                            setNotesValue(requestDetail.staff_notes || '');
                          }}
                          className="flex-1 px-2 py-1 bg-gray-200 text-gray-700 text-[10px] rounded hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600 line-clamp-4">
                      {requestDetail.staff_notes || (
                        <span className="italic text-gray-400">No notes</span>
                      )}
                    </p>
                  )}
                </div>
              </div>

              {/* Main Content */}
              <div className="flex-1 overflow-y-auto">
                {/* Error banner */}
                {error && (
                  <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between">
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                      <FiX className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Panel content */}
                {showAuditLog ? (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-gray-800">Audit Log</h3>
                      <button
                        onClick={() => setShowAuditLog(false)}
                        className="text-xs text-indigo-600 hover:text-indigo-700"
                      >
                        Back to Workflow
                      </button>
                    </div>
                    <AuditLogPanel requestId={requestId} authHeaders={authHeaders} />
                  </div>
                ) : (
                  <div className="p-4">
                    {renderPanel()}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center text-gray-500 py-12 w-full">
              Failed to load request details
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="flex items-center gap-4 text-xs text-gray-500">
            {requestDetail && (
              <>
                <span className="flex items-center gap-1">
                  <FiClock className="w-3 h-3" />
                  v{requestDetail.row_version}
                </span>
                <span>
                  Updated: {requestDetail.updated_at ? new Date(requestDetail.updated_at).toLocaleString() : 'N/A'}
                </span>
                <button
                  onClick={() => setShowAuditLog(!showAuditLog)}
                  className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
                >
                  <FiFileText className="w-3 h-3" />
                  {showAuditLog ? 'Hide Audit Log' : 'Audit Log'}
                </button>
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestWorkflowModal;
